import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import RupiahInput from "@/components/RupiahInput";
import { fmtDateTime, formatIDR } from "@/lib/constants";

function rowProjectLabel(pc) {
  if (pc.project_type === "variation_order") return `${pc.project_name} · VO ${pc.project_variation || ""}`.trim();
  if (pc.project_type === "working_order") return `${pc.project_name} · WO ${pc.project_wo_number || ""}`.trim();
  return pc.project_name || "Untitled";
}

/** BOD / FAT-L2 edits items on an existing petty cash. */
export default function PettyCashEditDialog({
  open, pc, items, note, onItemsChange, onNoteChange, onCancel, onSubmit,
}) {
  if (!pc) return null;
  const totalBaru = items.reduce((s, it) => s + (parseFloat(it.qty || 0) * parseFloat(it.unit_price || 0) || 0), 0);
  const setField = (i, field, value) => onItemsChange((s) => s.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="rounded-none max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="dlg-pc-edit">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><Pencil size={18} className="text-amber-600" /> Edit Item Petty Cash</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="bg-slate-50 border border-slate-200 px-3 py-2 text-xs">
            <div className="font-semibold text-slate-800">{pc.week_label} — {rowProjectLabel(pc)}</div>
            <div className="text-slate-500">Diajukan oleh {pc.user_name} · Total awal: <span className="font-semibold">{formatIDR(pc.total)}</span></div>
          </div>
          <div className="overflow-x-auto border border-slate-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-600 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="px-2 py-1 text-left w-1/3">Nama Item / Jasa</th>
                  <th className="px-2 py-1 text-right w-24">Qty</th>
                  <th className="px-2 py-1 text-right w-32">Harga Satuan</th>
                  <th className="px-2 py-1 text-right w-32">Subtotal</th>
                  <th className="px-2 py-1 text-left">Catatan</th>
                  <th className="px-2 py-1 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const sub = (parseFloat(it.qty || 0) * parseFloat(it.unit_price || 0)) || 0;
                  return (
                    <tr key={it._key || `pc-row-${i}`} className="border-t border-slate-100">
                      <td className="px-1 py-1"><Input data-testid={`edit-pc-name-${i}`} value={it.name} onChange={(e) => setField(i, "name", e.target.value)} className="rounded-none h-8 text-xs" /></td>
                      <td className="px-1 py-1"><Input data-testid={`edit-pc-qty-${i}`} type="number" min="0" step="1" value={it.qty} onChange={(e) => setField(i, "qty", e.target.value)} className="rounded-none h-8 text-sm text-right tabular-nums w-20" /></td>
                      <td className="px-1 py-1"><RupiahInput testid={`edit-pc-price-${i}`} value={it.unit_price} onChange={(v) => setField(i, "unit_price", v)} className="h-8 text-xs" /></td>
                      <td className="px-2 py-1 text-right tabular-nums text-slate-700">{formatIDR(sub)}</td>
                      <td className="px-1 py-1"><Input data-testid={`edit-pc-note-${i}`} value={it.note || ""} onChange={(e) => setField(i, "note", e.target.value)} className="rounded-none h-8 text-xs" placeholder="Opsional" /></td>
                      <td className="px-1 py-1 text-center">
                        {items.length > 1 && (
                          <button data-testid={`btn-edit-pc-remove-${i}`} onClick={() => onItemsChange((s) => s.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 size={12} /></button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50">
                  <td colSpan={3} className="px-2 py-1 text-right uppercase text-[10px] font-semibold tracking-wider">Total Baru</td>
                  <td className="px-2 py-1 text-right tabular-nums font-bold text-[#0B1D3A]" data-testid="edit-pc-total">{formatIDR(totalBaru)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <button data-testid="btn-edit-pc-add-item" onClick={() => onItemsChange((s) => [...s, { name: "", qty: 1, unit_price: 0, note: "" }])} className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:underline inline-flex items-center gap-1"><Plus size={12} /> Tambah Item</button>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Catatan Perubahan (wajib) *</label>
            <Textarea data-testid="input-pc-edit-note" value={note} onChange={(e) => onNoteChange(e.target.value)} className="rounded-none mt-1 text-sm" rows={2} placeholder="Jelaskan alasan perubahan…" />
          </div>
          {pc.edit_history?.length > 0 && (
            <div className="border-t border-slate-200 pt-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Riwayat Perubahan</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {pc.edit_history.map((h) => (
                  <div key={h.id} className="text-[10px] text-slate-600 border-l-2 border-amber-300 pl-2">
                    <span className="font-semibold">{h.editor_name}</span> ({h.editor_division} Lv{h.editor_level}) · {fmtDateTime(h.created_at)} · Total {formatIDR(h.prev_total)} → {formatIDR(h.new_total)}
                    <div className="italic">"{h.note}"</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-none">Batal</Button>
          <Button data-testid="btn-confirm-pc-edit" onClick={onSubmit} className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white">Simpan Perubahan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
